import { H5, H6 } from "@sellar-io/components";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { ProfilePicture, BuyerOnly } from "../../../../../components";
import {
  BestPricePromise,
  BestPricePromiseFullSize,
  BestPricePromiseCompact,
} from "../../../../../components/bestPricePromise/BestPricePromise";
import { BestPricePromiseDialog } from "../../../../../components/bestPricePromise/BestPricePromiseDialog";
import { ImageWithFallback } from "../../../../../components/image/imageWithFallback";
import {
  ProfilePictureCollection,
  ProfilePictureCollectionImage,
} from "../../../../../components/profilePictureCollection/profilePictureCollection";
import {
  SkeletonContainer,
  SkeletonAvatar,
  SkeletonImage,
  SkeletonText,
} from "../../../../../components/skeleton/Skeleton";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";

import { StorefrontDescriptionDialog } from "../StorefrontDialogs/StorefrontDescriptionDialog";
import { StorefrontSupplierBannerActionPanel } from "./StorefrontSupplierBannerActionPanel";

export const StorefrontSupplierBanner = ({
  supplier,
  isLoading,
}: {
  supplier: SupplierGetResponseType | undefined;
  isLoading: boolean;
}): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [active, setActive] = useState<boolean>(false);
  const [bestPricePromiseDialogActive, setBestPricePromiseDialogActive] =
    useState<boolean>(false);
  useEffect(() => {
    if (containerRef.current) {
      const { clientHeight, scrollHeight } = containerRef.current;
      if (clientHeight !== scrollHeight) return setIsClamped(true);
      setIsClamped(false);
    }
  }, [isLoading, isMobile, supplier?.description]);

  return (
    <>
      <div className={"flex justify-center bg-white pt-0 lg:p-8"}>
        <div className="h-auto w-full max-w-[1200px] items-center lg:flex lg:flex-row-reverse">
          <div className="h-auto items-center lg:w-1/2 lg:rounded-lg">
            {isLoading ? (
              <SkeletonContainer>
                <SkeletonImage className={"h-[300px]"} />
              </SkeletonContainer>
            ) : (
              <ImageWithFallback
                img={supplier?.imageUrl}
                fallback="supplier-hero"
                className={"max-h-[300px] w-full object-cover lg:rounded-lg"}
              />
            )}
          </div>
          <div className="flex flex-col gap-2 p-4 lg:w-1/2">
            <div className={"flex flex-row justify-between"}>
              <div className={"flex flex-row items-center gap-2"}>
                {isLoading ? (
                  <SkeletonContainer>
                    <SkeletonAvatar />
                    <div className="flex flex-col gap-2">
                      <SkeletonText className={"w-[75px]"} />
                      <SkeletonText className={"w-[120px]"} />
                    </div>
                  </SkeletonContainer>
                ) : (
                  <>
                    {/* here */}
                    <ProfilePicture
                      logoUrl={supplier?.Business.logoUrl}
                      name={supplier?.Business.name}
                      size={"large"}
                      fallback="supplier-badge"
                    />
                    <div className="flex flex-col">
                      <H5>{supplier?.Business.name}</H5>
                      <H6>
                        {supplier?.Business.DeliveryAddress.city}
                        {supplier?.Business.DeliveryAddress.region
                          ? `, ${supplier.Business.DeliveryAddress.region}`
                          : ""}
                      </H6>
                    </div>
                  </>
                )}
              </div>
              <BuyerOnly>
                <BestPricePromise
                  onClick={() => setBestPricePromiseDialogActive(true)}
                >
                  {isMobile ? (
                    <BestPricePromiseCompact />
                  ) : (
                    <BestPricePromiseFullSize />
                  )}
                </BestPricePromise>
              </BuyerOnly>
            </div>
            {supplier && (
              <div className="flex flex-wrap items-center gap-2">
                <ProfilePictureCollection className="flex flex-row">
                  {supplier.supplierProfileDetails.map((user, index) => (
                    <ProfilePictureCollectionImage
                      key={index}
                      imageUrl={user.profileImageUrl}
                      name={user.name}
                    />
                  ))}
                </ProfilePictureCollection>
                <H5>{supplier.supplierRunByString}</H5>
              </div>
            )}
            {isLoading ? (
              <SkeletonContainer className="flex flex-col gap-2">
                <SkeletonText />
                <SkeletonText />
                <SkeletonText className={"w-[100px]"} />
              </SkeletonContainer>
            ) : (
              <div className={"flex w-full flex-wrap items-end"}>
                <p className={"line-clamp-3"} ref={containerRef}>
                  {supplier?.description}
                </p>
                {isClamped && (
                  <p
                    onClick={() => setActive(true)}
                    className={
                      "mb-2 cursor-pointer font-semibold text-blue-800"
                    }
                  >
                    MORE
                  </p>
                )}
              </div>
            )}
            <StorefrontSupplierBannerActionPanel supplier={supplier} />
          </div>
        </div>
      </div>
      {supplier && (
        <StorefrontDescriptionDialog
          active={active}
          onDismiss={() => setActive(false)}
          supplier={supplier}
        />
      )}
      <BestPricePromiseDialog
        active={bestPricePromiseDialogActive}
        onDismiss={() => setBestPricePromiseDialogActive(false)}
        buyerName={business?.type === "retailer" ? business.name : null}
        supplierName={supplier?.Business.name}
        isStorefront={true}
      />
    </>
  );
};
